import { Helmet } from 'react-helmet-async';
import { generateWebPageSchema, generateOrganizationSchema, generateMultipleSchemas } from '@/lib/schema';

interface SeoHeadProps {
  title: string;
  description: string;
  occupation?: string;
  location?: string;
  salaryData?: {
    median: number;
    mean: number;
    min: number;
    max: number;
    currency?: string;
  };
  canonical?: string;
  noIndex?: boolean;
  image?: string;
  type?: 'website' | 'article';
  schemas?: string; // Pre-generated JSON-LD schemas string
}

const SeoHead = ({ 
  title, 
  description, 
  occupation, 
  location, 
  salaryData,
  canonical,
  noIndex = false,
  image,
  type = 'website',
  schemas
}: SeoHeadProps) => {
  
  const formatSalary = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const siteBase = 'https://wageatlas.com';

  // Enhanced JSON-LD structured data for SEO using schema utilities
  const generateStructuredData = () => {
    const schemaUrl = canonical || (typeof window !== 'undefined' ? `${siteBase}${window.location.pathname}` : siteBase);
    
    // Generate comprehensive WebPage schema with organization
    const webPageSchema = generateWebPageSchema(
      title,
      description,
      schemaUrl,
      {
        occupation,
        location,
        salaryData: salaryData ? {
          median: salaryData.median,
          mean: salaryData.mean,
          min: salaryData.min,
          max: salaryData.max,
          p10: salaryData.min,
          p25: salaryData.min + (salaryData.median - salaryData.min) * 0.25,
          p75: salaryData.median + (salaryData.max - salaryData.median) * 0.25,
          p90: salaryData.max,
          currency: salaryData.currency,
        } : undefined,
        baseUrl: siteBase,
      }
    );

    // Always include Organization schema for better SEO
    const organizationSchema = generateOrganizationSchema(siteBase);

    // Return both schemas as an array
    return generateMultipleSchemas([webPageSchema, organizationSchema]);
  };

  // Use provided schemas if available, otherwise generate them
  const structuredData = schemas || generateStructuredData();
  
  // Build canonical from explicit prop or current pathname
  let currentUrl: string;
  if (canonical) {
    // Normalize relative canonical props (e.g. "/rankings/") to absolute URLs
    currentUrl = canonical.startsWith('http')
      ? canonical
      : `${siteBase}${canonical.startsWith('/') ? '' : '/'}${canonical}`;
  } else if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    const search = window.location.search;
    currentUrl = `${siteBase}${pathname}${search}`;
  } else {
    currentUrl = siteBase;
  }
  
  // Normalize canonical URL to always have trailing slash (except root and URLs with query)
  try {
    const url = new URL(currentUrl);
    if (url.pathname !== '/' && !url.pathname.endsWith('/')) {
      url.pathname = `${url.pathname}/`;
      currentUrl = url.toString();
    }
  } catch {
    if (!currentUrl.endsWith('/') && !currentUrl.includes('?')) {
      currentUrl = `${currentUrl}/`;
    }
  }
  
  // Default OG image or use provided image
  const ogImage = image || `${siteBase}/og-image.png`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Wage Atlas" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* SEO directives */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={currentUrl} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {structuredData}
        </script>
      )}
      
      {/* Performance hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//googleads.g.doubleclick.net" />
    </Helmet>
  );
};

export default SeoHead;